import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { formatTenantType } from "../../../utils/tenantTypeFormatter";
import "./TenantsTable.scss";

const TenantsTable = ({
  data = [],
  loading = false,
  onSort,
  page = 0,
  rows = 10,
  totalRecords = 0,
  onPageChange,
  sortMeta = [],
}) => {
  const skeletonRows = [...Array(10)];

  const handleSort = (event) => {
    if (onSort) {
      onSort(event);
    }
  };

  const countryTemplate = (rowData) => {
    if (!rowData.country) return "-";
    return (
      <div className="flex align-items-center gap-2">
        <ReactCountryFlag
          countryCode={rowData.country}
          svg
          style={{ width: "20px", height: "20px" }}
        />
        <span>{getName(rowData.country) || rowData.country}</span>
      </div>
    );
  };

  const accountManagerTemplate = (rowData) => {
    // Try accountManagerData first, then fall back to account_manager
    const manager = rowData.accountManagerData || rowData.account_manager;
    if (!manager) return "-";

    // If preferred_name is present, show only that; otherwise show given_name + surname
    let name;
    if (manager.preferred_name) {
      name = manager.preferred_name;
    } else {
      const givenName = manager.given_name || "";
      const surname = manager.surname || "";
      name = `${givenName} ${surname}`.trim() || "-";
    }

    const managerId = manager.id;

    if (!managerId) return <span>{name}</span>;

    return (
      <Link
        to={`${rowData.id}/account-manager/${managerId}`}
        className="account-manager-link"
      >
        {name}
      </Link>
    );
  };

  const statusTemplate = (rowData) => {
    const status = rowData.status || "ACTIVE";
    const renderStatus = (status) => {
      switch (status) {
        case "ACTIVE":
          return (
            <>
              <span className="pi pi-circle-fill active-circle-fill"></span>{" "}
              Active
            </>
          );
        case "INACTIVE":
          return (
            <>
              <span className="pi pi-circle-fill not-active-circle-fill"></span>{" "}
              Not Active
            </>
          );
        default:
          return "-";
      }
    };

    return (
      <div className="flex align-items-center justify-content-between status-container">
        <div className="status-text">{renderStatus(status)}</div>
        {/* <i className="pi pi-pencil edit-icon" /> */}
      </div>
    );
  };

  return (
    <Card className="gen-card h-full w-full">
      <div className="tenants__table--container">
        <DataTable
          value={loading ? skeletonRows : data}
          sortMode="multiple"
          multiSortMeta={sortMeta}
          onSort={handleSort}
          responsiveLayout="scroll"
          emptyMessage="No tenants found"
          lazy
          paginator={totalRecords > 10}
          first={page * rows}
          rows={rows}
          totalRecords={totalRecords}
          onPage={onPageChange}
          rowsPerPageOptions={[10, 20, 30]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          className="p-datatable-striped"
        >
          <Column
            field="tenant_name"
            header="Tenant"
            body={(rowData) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                <Link to={`/tenants/${rowData.id}`} className="tenant-link">
                  {rowData.tenant_name}
                </Link>
              )
            }
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="type"
            header="Type"
            body={(rowData) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                formatTenantType(rowData.type)
              )
            }
            sortable
            style={{ width: "10%" }}
          />
          <Column
            field="total_number_of_users"
            header="Number of Users"
            body={(rowData) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                rowData.total_number_of_users || 0
              )
            }
            sortable
            style={{ width: "17%" }}
          />
          <Column
            field="country"
            header="Country"
            body={(rowData) =>
              loading ? <Skeleton width="100%" /> : countryTemplate(rowData)
            }
            sortable
            style={{ width: "15%" }}
          />
          <Column
            field="account_manager"
            sortField="account_manager"
            header="Account Manager"
            body={(rowData) =>
              loading ? (
                <Skeleton width="100%" />
              ) : (
                accountManagerTemplate(rowData)
              )
            }
            sortable
            style={{ width: "18%" }}
          />
          <Column
            field="status"
            header="Status"
            body={(rowData) =>
              loading ? <Skeleton width="100%" /> : statusTemplate(rowData)
            }
            sortable
            style={{ width: "15%" }}
          />
        </DataTable>
      </div>
    </Card>
  );
};

export default TenantsTable;
